// src/data/letterGroup.js
//
// Track 6 item 6a-09 (2026-09-14, task list section 228; ruling 130). data\YCP_LC_GC_WORKSPACE_
// REQUIREMENTS_2026-08-23.md SS6.3, Bob's words: "At initial notice level generate a group of letters
// with GC contact info in the header - General Committee, Local Committee, Railroad."
//
//   A GROUP from one trigger. The trigger is the initial notice - presentment of the claim on the
//   carrier (ruling 70, src\data\claimPresentment.js). The group is three letters: the notice to the
//   Railroad, and the copy to each of the two offices, each carrying the same three-block header.
//   THREE CONTACT BLOCKS, RESOLVED FROM PLATFORM DATA, NEVER TYPED: the General Committee from
//   offices/{committee} + officeTerms (holderOn, WO-001 S1) + committees/{committee}.seats; the Local
//   Committee from the claim's scopeKey the same way; the Railroad from a carrier contact record -
//   and NO SUCH RECORD EXISTS on this platform today, so that block resolves to NOT ON FILE and the
//   group is not sendable until one does. Saying so is the whole point.
//
// THE EVIDENTIARY STANDARD (data\YCP_EVIDENTIARY_STANDARD_2026-08-23.md) governs every line: "every
// proposition in an outgoing document must trace to a source on disk ... or be marked as unverified
// and not sent." So this module writes NO PROSE OF ITS OWN about the case. Every sentence in a letter
// is either (a) a field of the claim record, named as such, (b) rule text handed in verbatim from the
// catalogue resolver, or (c) fixed connective wording that asserts nothing about the facts. A field
// that is empty prints as [NOT ON FILE] and makes the group unsendable; nothing is guessed, filled,
// or predicted.
//
// Pure: no React, no Firestore. The caller (LetterGroupPanel) reads the documents and hands them in.

import { holderOn, officeStateOn } from './officeSuccession';
import { claimCommittee, claimYard } from './claimPayload';
import { getClaimRequirements } from './claimRequirements';
import { renderLetterheadBanner } from './letterhead';
import { markDraft } from './signoff';

export const NOT_ON_FILE = '[NOT ON FILE]';

function nonEmpty(v) { return v !== null && v !== undefined && String(v).trim() !== ''; }
function field(claim, key, label, missing) {
  const v = claim ? claim[key] : undefined;
  if (!nonEmpty(v)) { missing.push(label); return NOT_ON_FILE; }
  return String(v).trim();
}

// ---- contact blocks -------------------------------------------------------------------------

function officeBlock(kind, officeId, offices, terms, seats, asOf) {
  const office = (offices || []).find(o => o && o.officeId === officeId) || null;
  const st = office ? officeStateOn(office, terms, asOf) : { state: 'not_of_record', term: null };
  const term = st.term || (office ? null : holderOn(terms, officeId, asOf));
  const seat = (seats || []).find(s => s && (kind === 'gc' ? s.role === 'general_chair' : (s.role === 'local_chair' && (s.scopeKey === officeId || (Array.isArray(s.scopes) && s.scopes.includes(officeId)))))) || null;
  const lines = [];
  const sources = [];
  const title = kind === 'gc' ? 'General Chairperson' : 'Local Chairperson';
  const label = office ? office.label : (title + ', ' + (officeId || NOT_ON_FILE));
  lines.push(label);
  let resolved = false;
  if (term && term.holderName) {
    lines.push((st.acting ? 'Acting: ' : '') + term.holderName);
    sources.push('officeTerms/' + (term.termId || '?'));
    resolved = true;
    if (term.holderEmail) lines.push(term.holderEmail);
    else if (seat && seat.email) { lines.push(seat.email); sources.push('committees.seats'); }
  } else if (seat && seat.name) {
    lines.push(seat.name);
    if (seat.email) lines.push(seat.email);
    sources.push('committees.seats (no term on record - WO-001 S1 not yet attested for this seat)');
    resolved = true;
  } else if (st.state === 'vacant') {
    lines.push('VACANT' + (st.reason ? ' - ' + st.reason : ''));
    sources.push('offices/' + officeId + '.status');
  } else {
    lines.push(NOT_ON_FILE);
  }
  if (office && office.territory) lines.push(office.railroad + ' - ' + office.territory);
  return { kind: kind === 'gc' ? 'general_committee' : 'local_committee', officeId: officeId || null, resolved, lines, sources, state: st.state };
}

function railroadBlock(claim, carrierContacts) {
  const rr = claim && claim.railroad;
  const contact = (carrierContacts || []).find(c => c && rr && c.railroad === rr) || null;
  if (contact && contact.name) {
    return { kind: 'railroad', resolved: true, lines: [rr, contact.name, contact.title || '', contact.address || '', contact.email || ''].filter(Boolean), sources: ['carrierContacts/' + (contact.id || rr)] };
  }
  return { kind: 'railroad', resolved: false, lines: [rr || NOT_ON_FILE, 'Carrier contact ' + NOT_ON_FILE + ' - no carrier contact record exists on this platform'], sources: [] };
}

// The three blocks. `asOf` is the filing date: the office of record on the day the claim was filed,
// the same question OfficeOfRecord asks on the claim page.
export function resolveContactBlocks(claim, ctx) {
  const c = ctx || {};
  const committee = claimCommittee(claim);
  const scopeKey = claim && claim.scopeKey;
  const asOf = c.asOf || (claim && (claim.filedAt || claim.createdAt)) || null;
  return {
    generalCommittee: committee ? officeBlock('gc', committee, c.offices, c.terms, c.seats, asOf) : { kind: 'general_committee', resolved: false, lines: ['General Chairperson ' + NOT_ON_FILE + ' - the claim carries no committee'], sources: [], state: 'not_of_record' },
    localCommittee: scopeKey ? officeBlock('lc', scopeKey, c.offices, c.terms, c.seats, asOf) : { kind: 'local_committee', resolved: false, lines: ['Local Chairperson ' + NOT_ON_FILE + ' - the claim carries no scopeKey'], sources: [], state: 'not_of_record' },
    railroad: railroadBlock(claim, c.carrierContacts),
  };
}

// ---- the letters ------------------------------------------------------------------------------

// RULING 188 (2026-09-19, Bob): every document the platform generates to communicate names the parent
// International at its head - "the legal and ethical thing to do". The wording is Bob's approval of the
// proposal in data\YCP_PLANS_2026-09-19.md. ONE constant; every generator reads it (§XI). Ruling 185: it
// comes OFF the platform's own screens.
export const PARENT_ORGANIZATION = 'SMART Transportation Division (SMART-TD)';

export function letterHeader(blocks, letterhead) { return header(blocks, letterhead); }

// THE TWO OFFICE BLOCKS ALONE, without the carrier. Split out 2026-09-22 (section 347) for the onboarding
// letters of ruling 208: an application is between a man and his union, and the carrier is not a party to
// it, so those letters carry no RAILROAD block. Split rather than copied - one header, two lengths (XI).
//
// LETTERHEAD (ruling 229, 2026-09-25). An optional per-committee letterhead the GC set (src\data\
// letterhead.js) prints as a banner ABOVE the parent International and the resolved office blocks. When
// none is set the output is byte-for-byte what it was before, so every existing caller and its suite is
// unchanged; the resolved office-of-record blocks below are never altered by it (honesty: the banner
// adds, it does not replace what is sourced from the spine).
export function officeHeader(blocks, letterhead) {
  const col = b => (b && b.lines ? b.lines.join('\n') : NOT_ON_FILE);
  const banner = renderLetterheadBanner(letterhead);
  const base = PARENT_ORGANIZATION + '\n\n' + 'GENERAL COMMITTEE\n' + col(blocks.generalCommittee) + '\n\nLOCAL COMMITTEE\n' + col(blocks.localCommittee);
  return banner ? banner + '\n\n' + base : base;
}

function header(blocks, letterhead) {
  const col = b => (b && b.lines ? b.lines.join('\n') : NOT_ON_FILE);
  return officeHeader(blocks, letterhead) + '\n\nRAILROAD\n' + col(blocks.railroad);
}

// Rule text is handed in already resolved (ruleTexts: [{ rule, text, status, qualifier }]) by the
// caller through src\data\ruleResolver.js - this module never composes rule language.
function rulesSection(claim, ruleTexts, missing) {
  const relied = (Array.isArray(claim && claim.matchedRules) ? claim.matchedRules : []).filter(r => r && !r.setAsideAt);
  if (!relied.length) { missing.push('rule relied on'); return 'RULE(S) RELIED ON\n' + NOT_ON_FILE; }
  return 'RULE(S) RELIED ON\n' + relied.map(r => {
    const t = (ruleTexts || []).find(x => x && x.rule === r.rule) || null;
    if (!t || !nonEmpty(t.text)) { missing.push('text of ' + (r.rule || r.ruleId)); return (r.rule || r.ruleId) + '\n' + NOT_ON_FILE + ' - the catalogue holds no text for this rule'; }
    return (r.rule || r.ruleId) + (t.status && t.status !== 'active' ? ' [' + t.status + ']' : '') + '\n"' + t.text.trim() + '"' + (t.qualifier ? '\n(' + t.qualifier + ')' : '');
  }).join('\n\n');
}

// A-9 SECOND HALF (2026-09-21). THE LETTER STATES WHAT THE AGREEMENT REQUIRES THE FILER TO FURNISH.
//
// Section 322 filed A-9 as two defects. The first - that the FORM does not check CSX's eight items -
// was read against the source and WITHDRAWN: claimRequirements.js carries all eight and REQ-1 proves
// each is capturable. The second stands and is closed here: the letter named only "Amount claimed",
// while CSX Article 20(b) requires the filer to FURNISH eight. A letter that omits seven of them
// furnishes less than the agreement asks for.
//
// DRIVEN BY THE REQUIREMENTS, NOT BY A COPY OF THEM. The items come from claimRequirements.js, the
// same list the Eligibility/Payable panel ticks. Hardcoding CSX's eight here would put one fact in
// two places (directives XI) and would leave every other agreement out; as written, an agreement
// catalogued tomorrow states its own items with no change to this file.
//
// An item with nothing behind it prints [NOT ON FILE] and pushes to `missing`, which is what holds
// `sendable` false. It is never smoothed over, and the letter never claims to furnish what it lacks.
function requiredItemsSection(claim, missing) {
  const req = getClaimRequirements(claim && claim.applicableAgreement);
  const items = (req && Array.isArray(req.payable)) ? req.payable : [];
  if (!items.length) return '';            // uncatalogued agreement -> nothing, never a bluff
  const rule = (req && req.claimsRule) ? req.claimsRule : 'the governing claims rule';
  const lines = items.map((it) => {
    const keys = Array.isArray(it.field) ? it.field : [it.field];
    const vals = keys
      .filter((k) => k && k.indexOf('__') !== 0)
      .map((k) => {
        const v = claim ? claim[k] : undefined;
        if (k === 'matchedRules') {
          const relied = (Array.isArray(v) ? v : []).filter((r) => r && !r.setAsideAt);
          return relied.length ? relied.map((r) => r.rule || r.ruleId).join('; ') : '';
        }
        return nonEmpty(v) ? String(v).trim() : '';
      })
      .filter((s) => s !== '');
    if (!vals.length) { missing.push(it.label); return it.label + ': ' + NOT_ON_FILE; }
    return it.label + ': ' + vals.join(', ');
  });
  return 'INFORMATION FURNISHED, AS ' + rule + ' REQUIRES\n' + lines.join('\n');
}

function body(claim, ruleTexts, missing) {
  const number = field(claim, 'id', 'claim number', missing);
  const claimant = field(claim, 'claimantName', 'claimant', missing);
  const date = field(claim, 'incidentDate', 'incident date', missing);
  const yard = claimYard(claim); if (!nonEmpty(yard)) missing.push('work location');
  const agreement = field(claim, 'applicableAgreement', 'agreement', missing);
  const amount = nonEmpty(claim && claim.amountClaimed) ? String(claim.amountClaimed) : (missing.push('amount claimed'), NOT_ON_FILE);
  const description = field(claim, 'description', 'statement of claim', missing);
  return [
    'CLAIM ' + number,
    'Claimant: ' + claimant,
    'Date of occurrence: ' + date,
    'Location: ' + (nonEmpty(yard) ? yard : NOT_ON_FILE),
    'Agreement: ' + agreement,
    'Amount claimed: ' + amount,
    '',
    'STATEMENT OF CLAIM (as filed by the claimant; verbatim from the claim record)',
    description,
    '',
    rulesSection(claim, ruleTexts, missing),
  ].concat(requiredItemsSection(claim, missing) ? ['', requiredItemsSection(claim, missing)] : []).join('\n');
}

const FOOT = '\n\nEvery statement above is taken from the claim record and the agreement catalogue as filed on this platform; nothing is added, paraphrased or inferred (data\\YCP_EVIDENTIARY_STANDARD_2026-08-23.md).';

// The group. `sendable` is false while any block or field is unresolved; a caller must never mark an
// unsendable group approved (the [VERIFY] guard of the standard, applied to NOT ON FILE).
// `opts.letterhead` (ruling 229) prints the committee's letterhead banner above the header. Every letter
// is a DRAFT until the officer signs off on it (ruling 231): markDraft() stamps each one so a copied or
// downloaded preview can never be mistaken for a signed letter. The panel's sign-off action affixes the
// signature; this module signs nothing and imports no way to.
export function presentmentLetterGroup(claim, blocks, ruleTexts, opts) {
  const missing = [];
  const b = blocks || resolveContactBlocks(claim, {});
  const hdr = header(b, opts && opts.letterhead);
  const bod = body(claim, ruleTexts, missing);
  const number = nonEmpty(claim && claim.id) ? claim.id : NOT_ON_FILE;
  const unresolvedBlocks = ['generalCommittee', 'localCommittee', 'railroad'].filter(k => !b[k].resolved).map(k => b[k].kind);
  const letters = [
    { to: 'railroad', title: 'Notice of claim ' + number + ' - to the Railroad', text: markDraft(hdr + '\n\nTO: ' + b.railroad.lines.join(', ') + '\n\nNOTICE OF CLAIM\n\n' + bod + FOOT) },
    { to: 'general_committee', title: 'Notice of claim ' + number + ' - copy to the General Committee', text: markDraft(hdr + '\n\nTO: ' + b.generalCommittee.lines.join(', ') + '\nCOPY - notice served on the Railroad as above.\n\n' + bod + FOOT) },
    { to: 'local_committee', title: 'Notice of claim ' + number + ' - copy to the Local Committee', text: markDraft(hdr + '\n\nTO: ' + b.localCommittee.lines.join(', ') + '\nCOPY - notice served on the Railroad as above.\n\n' + bod + FOOT) },
  ];
  const uniq = Array.from(new Set(missing));
  return { ok: true, letters, unresolvedBlocks, missingFields: uniq, sendable: unresolvedBlocks.length === 0 && uniq.length === 0 };
}
